import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calendar, Loader2, AlertCircle, ChevronDown, ChevronRight } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useProgramYear } from "@/contexts/ProgramYearContext";
import { useAuth } from "@/contexts/AuthContext";

export function ProgramYearSettings() {
  const { toast } = useToast();
  const { operatingYear, loading, updateProgramYear } = useProgramYear();
  const { user } = useAuth();
  const [selectedYear, setSelectedYear] = useState<string>(operatingYear.toString());
  const [saving, setSaving] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    setSelectedYear(operatingYear.toString());
  }, [operatingYear]);

  const yearOptions = Array.from({ length: 11 }, (_, i) => 2020 + i);

  const handleSave = async () => {
    const year = parseInt(selectedYear);
    if (year === operatingYear) {
      toast({ title: "No Changes", description: "Program year is already set to " + year });
      return;
    }
    setSaving(true);
    try {
      await updateProgramYear(year);
      toast({
        title: "Program Year Updated",
        description: `Operating year is now ${year}. New data and calculations use this year.`,
      });
    } catch (error) {
      console.error('Error saving program year:', error);
      toast({ title: "Save Failed", description: "Could not update program year. Please try again.", variant: "destructive" });
      setSelectedYear(operatingYear.toString());
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = selectedYear !== operatingYear.toString();

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Calendar className="h-5 w-5" />Program Year</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Calendar className="h-5 w-5" />Program Year</CardTitle>
        <CardDescription>
          The operating year — new uploads and calculations default here, and it's where everyone starts each session.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {!isAdmin && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>Only administrators can change the operating year. Current: {operatingYear}.</AlertDescription>
          </Alert>
        )}

        {/* Current year + change control */}
        <div className="flex flex-wrap items-center gap-4 rounded-lg border bg-muted/30 p-4">
          <div className="flex items-baseline gap-3">
            <span className="text-3xl font-semibold tracking-tight">{operatingYear}</span>
            <span className="text-xs text-muted-foreground">current operating year</span>
          </div>

          {isAdmin && (
            <div className="flex items-end gap-2 ml-auto">
              <div className="space-y-1">
                <Label htmlFor="program-year" className="text-xs text-muted-foreground">Change to</Label>
                <Select value={selectedYear} onValueChange={setSelectedYear} disabled={saving}>
                  <SelectTrigger id="program-year" className="w-28 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {yearOptions.map((year) => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleSave} disabled={!hasChanges || saving} className="h-9">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save
              </Button>
              {hasChanges && (
                <Button variant="ghost" className="h-9" onClick={() => setSelectedYear(operatingYear.toString())} disabled={saving}>
                  Cancel
                </Button>
              )}
            </div>
          )}
        </div>

        {isAdmin && hasChanges && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Changing the operating year affects new calculations. The performance period becomes Jan 1 – Dec 31, {selectedYear}.
              (Users can still view other years from the top bar.)
            </AlertDescription>
          </Alert>
        )}

        {/* Collapsible help */}
        <div className="rounded-lg border">
          <button
            type="button"
            onClick={() => setHelpOpen(o => !o)}
            className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            {helpOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            How the program year works
          </button>
          {helpOpen && (
            <ul className="px-4 pb-4 text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>Measurement period: Jan 1 – Dec 31 of the program year.</li>
              <li>Patient ages calculated as of Dec 31 of the program year.</li>
              <li>Performance rates use services within the program year; lookbacks are relative to it.</li>
              <li>Users can view any year with data via the top-bar selector; this just sets the default.</li>
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
